import { HELPER_CITY_GRID_SEARCH_OFFSET_MILES, HELPER_CITY_GRID_SEARCH_MAX_PROBES, MILES_PER_LATITUDE_DEGREE, toRadians, buildCityGridSearchCoordinates, normalizeLocationViewportBounds, getDistanceMiles, buildCityViewportGridSearchCoordinates } from "./locationSearchGeometry";
import {
  isPointInPolygon,
  getEmployeeCoverageAreas,
  shouldIncludeHelperInMemberList,
  getEmployeeStatus
} from "./schedulingCoreClient";
import {
  collection,
  endAt,
  endBefore,
  getDocs,
  orderBy,
  query,
  startAt,
  Timestamp,
  where,
} from "firebase/firestore";
import { DateTime } from "luxon";
import { Loader } from "@googlemaps/js-api-loader";
import moment from "moment-timezone";
import tzlookup from "tz-lookup";
import { db } from "../firebase";
import { isHelperSuspended } from "./helperStatus";
import {
  hasValidCoordinates,
  isCityLevelHelperLocationSearch,
} from "./locationSearchMode";

import { geohashForLocation, geohashQueryBounds } from "geofire-common";
import geohash from "ngeohash";
import {
  GOOGLE_MAPS_JS_API_LIBRARIES,
  GOOGLE_MAPS_VERSION,
} from "../utils/googleMapsConfig";
import {
  evaluateStreetAddressGeocodeResult,
  isStreetAddressGeocodeResult,
} from "./streetAddressGeocodeValidation";

export { HELPER_CITY_GRID_SEARCH_OFFSET_MILES, HELPER_CITY_GRID_SEARCH_MAX_PROBES, buildCityGridSearchCoordinates, normalizeLocationViewportBounds, getDistanceMiles, buildCityViewportGridSearchCoordinates } from "./locationSearchGeometry";

export {
  isPointInPolygon,
  doesLineExitPolygon,
  findAreas,
  getFutureAvailability,
  getCurrentArea,
  buildCoverageAreasFromPublicTimeline,
  getEmployeeCoverageAreas,
  getCoverageWindowsForPoint,
  shouldIncludeHelperInMemberList,
  getEmployeeStatus,
  getFutureAvailabilityForCoverageWindows,
  filterAvailabilityWindowsByCoverage,
  isRangeWithinCoverageWindows,
  distanceBetweenGeohashes
} from "./schedulingCoreClient";

export { isCityLevelHelperLocationSearch } from "./locationSearchMode";

const BASE32 = "0123456789bcdefghjkmnpqrstuvwxyz";
const BITS = [16, 8, 4, 2, 1];
const MIN_DATE = DateTime.fromISO("0001-01-01T00:00:00.000Z");
const MAX_DATE = DateTime.fromISO("9999-12-31T23:59:59.999Z");


export const HELPER_CITY_GRID_SEARCH_BATCH_SIZE = 4;
export const CITY_VIEWPORT_LOOKUP_TIMEOUT_MS = 1800;
export const HELPER_LOCATION_LOOKUP_TIMEOUT_MS = 5000;
const CITY_VIEWPORT_CACHE = new Map();
let googleMapsJsLoaderPromise = null;
let googleMapsJsLoaderKey = "";
export const GEOHASH_SUBSTRING_KEYS = [
  "sub6",
  "sub5",
  "sub4",
  "sub3",
  "sub2",
  "sub1",
];

const normalizeStringArray = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  ).sort();

const isTestEnvironment = () => process.env.NODE_ENV === "test";

const shouldUseBrowserGeocoder = () =>
  !isTestEnvironment() &&
  typeof window !== "undefined" &&
  typeof document !== "undefined";

const readLatLngCoordinate = (value, axis) => {
  const raw =
    typeof value?.[axis] === "function" ? value[axis]() : value?.[axis];
  const number = Number(raw);
  return Number.isFinite(number) ? number : null;
};

const normalizeGoogleMapsJsViewport = (viewport) => {
  if (!viewport) return null;
  const northEast =
    typeof viewport.getNorthEast === "function"
      ? viewport.getNorthEast()
      : viewport.northeast || viewport.northEast || null;
  const southWest =
    typeof viewport.getSouthWest === "function"
      ? viewport.getSouthWest()
      : viewport.southwest || viewport.southWest || null;
  const north = readLatLngCoordinate(northEast, "lat");
  const east = readLatLngCoordinate(northEast, "lng");
  const south = readLatLngCoordinate(southWest, "lat");
  const west = readLatLngCoordinate(southWest, "lng");

  return [north, east, south, west].every(Number.isFinite)
    ? {
        northeast: { lat: north, lng: east },
        southwest: { lat: south, lng: west },
      }
    : viewport;
};

const normalizeGoogleMapsJsGeocodeResult = (result = {}) => {
  const lat = readLatLngCoordinate(result?.geometry?.location, "lat");
  const lng = readLatLngCoordinate(result?.geometry?.location, "lng");
  return {
    ...result,
    address_components: result.address_components || [],
    geometry: {
      ...(result.geometry || {}),
      location:
        Number.isFinite(lat) && Number.isFinite(lng)
          ? { lat, lng }
          : result?.geometry?.location,
      viewport: normalizeGoogleMapsJsViewport(result?.geometry?.viewport),
    },
  };
};

const loadGoogleMapsJs = async (apiKey) => {
  if (window?.google?.maps?.Geocoder) return window.google.maps;
  const loaderKey = `${apiKey}:${GOOGLE_MAPS_VERSION}:${GOOGLE_MAPS_JS_API_LIBRARIES.join(",")}`;
  if (!googleMapsJsLoaderPromise || googleMapsJsLoaderKey !== loaderKey) {
    const loader = new Loader({
      apiKey,
      version: GOOGLE_MAPS_VERSION,
      libraries: GOOGLE_MAPS_JS_API_LIBRARIES,
    });
    googleMapsJsLoaderKey = loaderKey;
    googleMapsJsLoaderPromise = Promise.resolve()
      .then(() => loader.load())
      .then(() => window?.google?.maps)
      .catch((error) => {
        googleMapsJsLoaderPromise = null;
        googleMapsJsLoaderKey = "";
        throw error;
      });
  }
  return googleMapsJsLoaderPromise;
};

const browserGeocode = async ({ address = "", location = null, apiKey }) => {
  const maps = await loadGoogleMapsJs(apiKey);
  if (!maps?.Geocoder) {
    throw new Error("Google Maps JavaScript Geocoder is unavailable.");
  }
  const geocoder = new maps.Geocoder();
  const request = {
    region: "us",
    componentRestrictions: { country: "US" },
    ...(address ? { address } : {}),
    ...(location ? { location } : {}),
  };
  const response = await new Promise((resolve, reject) => {
    geocoder.geocode(request, (results, status) => {
      if (status === "OK") {
        resolve({ status, results: results || [] });
        return;
      }
      reject(new Error(`Google Maps JavaScript geocode failed: ${status}`));
    });
  });
  return {
    status: response.status,
    results: response.results.map(normalizeGoogleMapsJsGeocodeResult),
  };
};

const fetchGoogleGeocodeJson = async ({
  address = "",
  location = null,
  apiKey,
  timeoutMs = null,
  useBrowserGeocoder = true,
} = {}) => {
  if (!apiKey) return { status: "REQUEST_DENIED", results: [] };
  if (useBrowserGeocoder && shouldUseBrowserGeocoder()) {
    return browserGeocode({ address, location, apiKey });
  }

  const url = new URL("https://maps.googleapis.com/maps/api/geocode/json");
  if (address) url.searchParams.set("address", address);
  if (location)
    url.searchParams.set("latlng", `${location.lat},${location.lng}`);
  url.searchParams.set("components", "country:US");
  url.searchParams.set("region", "us");
  url.searchParams.set("key", apiKey);

  if (timeoutMs == null) {
    const response = await fetch(url.toString());
    return response.json();
  }
  return fetchCityViewportJson(url.toString(), { timeoutMs });
};

export const feetToMeters = (feet = 0) => Number(feet || 0) * 0.3048;

export function buildGeohashMap(value = "") {
  const geohashValue = String(value || "").trim();
  const buildSub = (length) =>
    geohashValue.length >= length ? geohashValue.slice(0, length) : "";

  return {
    sub6: buildSub(6),
    sub5: buildSub(5),
    sub4: buildSub(4),
    sub3: buildSub(3),
    sub2: buildSub(2),
    sub1: buildSub(1),
  };
}

export function buildLocationSummary({
  city = "",
  state = "",
  stateCode = "",
  country = "",
  countryCode = "",
  zipCode = "",
  geohash = "",
} = {}) {
  const geohashMap = buildGeohashMap(geohash);

  return {
    city,
    state,
    stateCode,
    country,
    countryCode,
    zipCode,
    geohash:
      geohashMap.sub6 ||
      String(geohash || "")
        .trim()
        .slice(0, 6),
    geohashMap,
  };
}

export function buildLocationDetails({
  line1 = "",
  line2 = "",
  city = "",
  state = "",
  stateCode = "",
  country = "",
  countryCode = "",
  zipCode = "",
  directions = "",
  lat = null,
  lng = null,
  geohash = "",
} = {}) {
  return {
    line1,
    line2,
    city,
    state,
    stateCode,
    country,
    countryCode,
    zipCode,
    directions,
    lat,
    lng,
    geohash: String(geohash || "").trim(),
    geohashMap: buildGeohashMap(geohash),
  };
}

const normalizeLocationText = (value = "") =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const SERVICE_AREA_CITY_COMPONENT_PRIORITY = [
  "locality",
  "postal_town",
  "sublocality_level_1",
  "sublocality",
  "neighborhood",
  "administrative_area_level_3",
  "administrative_area_level_2",
];

const getAddressComponent = (
  components = [],
  type = "",
  field = "long_name",
) => {
  const component = (Array.isArray(components) ? components : []).find(
    (entry) => Array.isArray(entry?.types) && entry.types.includes(type),
  );
  return normalizeLocationText(component?.[field] || "");
};

const getAddressComponentFromResults = (
  results = [],
  componentTypes = [],
  field = "long_name",
) => {
  for (const type of componentTypes) {
    for (const result of Array.isArray(results) ? results : []) {
      const value = getAddressComponent(
        result?.address_components || [],
        type,
        field,
      );
      if (value) return value;
    }
  }

  return "";
};

export const resolveServiceAreaLocationLabelFromGeocodeResults = (
  results = [],
) => ({
  city: getAddressComponentFromResults(
    results,
    SERVICE_AREA_CITY_COMPONENT_PRIORITY,
  ),
  state:
    getAddressComponentFromResults(
      results,
      ["administrative_area_level_1"],
      "short_name",
    ) ||
    getAddressComponentFromResults(results, ["administrative_area_level_1"]),
  stateCode: getAddressComponentFromResults(
    results,
    ["administrative_area_level_1"],
    "short_name",
  ),
  zipCode: getAddressComponentFromResults(results, ["postal_code"]),
  country: getAddressComponentFromResults(results, ["country"]),
  countryCode: getAddressComponentFromResults(
    results,
    ["country"],
    "short_name",
  ),
});

export function normalizeManualLocationData(newData = {}, previousData = {}) {
  const latitude = Number(newData?.latitude ?? newData?.lat ?? 0) || 0;
  const longitude = Number(newData?.longitude ?? newData?.lng ?? 0) || 0;
  const line1 = normalizeLocationText(newData?.line1 || "");
  const line2 = normalizeLocationText(newData?.line2 || "");
  const city = normalizeLocationText(newData?.city || newData?.locality || "");
  const state = normalizeLocationText(
    newData?.state || newData?.region || newData?.stateName || "",
  );
  const rawStateCode = normalizeLocationText(
    newData?.stateCode || newData?.region_code || newData?.state_code || "",
  );
  const stateCode = normalizeLocationText(
    convertStateNameToAbbreviation(rawStateCode || state),
  ).toUpperCase();
  const zipCode = normalizeLocationText(
    newData?.zipCode || newData?.postal || "",
  );
  const country = normalizeLocationText(
    newData?.country || previousData?.country || "",
  );
  const countryCode = normalizeLocationText(
    newData?.countryCode ||
      newData?.country_code ||
      previousData?.countryCode ||
      previousData?.country_code ||
      "",
  ).toUpperCase();
  const explicitAddressType = normalizeLocationText(
    newData?.addressType,
  ).toLowerCase();
  const addressType =
    explicitAddressType === "home" || explicitAddressType === "city"
      ? explicitAddressType
      : line1
        ? "home"
        : "city";
  const geohash = normalizeLocationText(
    newData?.geohash ||
      (hasValidCoordinates(latitude, longitude)
        ? geohashForLocation([latitude, longitude])
        : ""),
  );
  const viewport = normalizeLocationViewportBounds(
    newData?.viewport ||
      newData?.cityViewport ||
      newData?.geometry?.viewport ||
      newData?.geometryViewport ||
      null,
  );

  return {
    ...(previousData || {}),
    latitude,
    longitude,
    lat: latitude,
    lng: longitude,
    line1,
    line2,
    city,
    locality: city,
    state: state || stateCode,
    region: state || stateCode,
    stateCode,
    region_code: stateCode,
    zipCode,
    postal: zipCode,
    country,
    countryCode,
    country_code: countryCode,
    geohash,
    addressType,
    neighborhood: normalizeLocationText(newData?.neighborhood || ""),
    formattedAddress: normalizeLocationText(newData?.formattedAddress || ""),
    placeId: normalizeLocationText(newData?.placeId || ""),
    viewport,
    locationSource: "manual",
  };
}

export function decodeGeoHash(geohash) {
  const bounds = decodeGeoHashToBounds(geohash);
  if (bounds.length === 0) return [0, 0];
  const lat = (bounds.sw.lat + bounds.ne.lat) / 2;
  const lng = (bounds.sw.lng + bounds.ne.lng) / 2;

  return [lat, lng];
}

export const getCityStateFromZip = async (zipCode) => {
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

  try {
    const data = await fetchGoogleGeocodeJson({
      address: String(zipCode || ""),
      apiKey,
    });

    console.log("data: ", data);

    if (data.status === "OK") {
      const addressComponents = data.results[0].address_components;

      // Initialize variables to hold city and state
      let city = "";
      let state = "";

      // Extract city and state from address components
      for (let component of addressComponents) {
        if (component.types.includes("locality")) {
          city = component.long_name; // City name
        }
        if (component.types.includes("administrative_area_level_1")) {
          state = component.short_name; // State abbreviation
        }
      }

      if (city && state) {
        return `${city}, ${state}`;
      }

      return "City or state not found";
    } else {
      return zipCode;
    }
  } catch (error) {
    console.error("Error fetching city:", error);
    return zipCode;
  }
};

export function buildGeohashNeighborsFromPoint(lat, lng, precision = 4) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    throw new Error(`Invalid lat/lng: lat=${lat}, lng=${lng}`);
  }
  if (!Number.isInteger(precision) || precision < 1) {
    throw new Error(`Invalid precision: ${precision}`);
  }

  // NOTE: node-geohash's encode signature is encode(lat, lon, [numberOfChars])
  const center = geohash.encode(lat, lng, precision);

  if (typeof geohash.neighbors !== "function") {
    throw new Error(
      "ngeohash.neighbors is not available. Confirm you're using the 'ngeohash' (node-geohash) package.",
    );
  }

  const arr = geohash.neighbors(center); // [n, ne, e, se, s, sw, w, nw]
  const [n, ne, e, se, s, sw, w, nw] = arr;

  const neighbors = { n, ne, e, se, s, sw, w, nw };
  const ordered = [center, n, ne, e, se, s, sw, w, nw];

  // Uniquify (rare edge cases near poles/dateline)
  const all = Array.from(new Set(ordered));

  return { precision, center, neighbors, all };
}

export const isStreetLevelGeocodeResult = isStreetAddressGeocodeResult;

export const getLocationDetailsFromPartialAddress = async (address) => {
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

  // Ensure the address contains at least one valid field
  const { line1, line2, city, state, zipCode } = address || {};
  if (!line1 && !line2 && !city && !state && !zipCode) {
    throw new Error("Address must contain at least one field");
  }

  // Construct the query string based on available address fields
  const query = [line1, line2, city, state, zipCode]
    .filter(Boolean) // Remove any empty fields
    .join(", ");

  try {
    const data = await fetchGoogleGeocodeJson({
      address: query,
      apiKey,
    });

    console.log("data: ", data);

    if (data.status === "OK") {
      const result = data.results[0] || {};
      const streetValidation = evaluateStreetAddressGeocodeResult(
        result,
        address,
      );
      if (!streetValidation.valid) return null;
      const location = result.geometry.location; // Extract lat/lng
      const lat = location.lat;
      const lng = location.lng;
      const geohash = geohashForLocation([lat, lng]);
      const addressComponents = Array.isArray(result.address_components)
        ? result.address_components
        : [];
      const getComponent = (type, field = "long_name") => {
        const component = addressComponents.find((entry) =>
          entry.types.includes(type),
        );
        return component?.[field] || "";
      };
      const stateCode = getComponent(
        "administrative_area_level_1",
        "short_name",
      );
      const state = getComponent("administrative_area_level_1");
      const country = getComponent("country", "long_name");
      const countryCode = getComponent("country", "short_name");
      const city =
        getComponent("locality") ||
        getComponent("postal_town") ||
        getComponent("sublocality_level_1") ||
        getComponent("administrative_area_level_3");
      const zipCode = getComponent("postal_code");
      const streetNumber = getComponent("street_number");
      const route = getComponent("route");
      const premise = getComponent("premise");
      const subpremise = getComponent("subpremise");

      return {
        line1: streetNumber ? `${streetNumber} ${route}`.trim() : premise,
        line2: subpremise,
        lat,
        lng,
        latitude: lat,
        longitude: lng,
        geohash,
        city,
        state,
        stateCode,
        zipCode,
        country,
        countryCode,
        formattedAddress: normalizeLocationText(result.formatted_address || ""),
        placeId: normalizeLocationText(result.place_id || ""),
        viewport: normalizeGoogleMapsJsViewport(result.geometry?.viewport),
        addressReviewRequired: streetValidation.requiresReview === true,
      };
    } else {
      console.error("Geocoding API error:", data.status);
      return null; // Return null if the API cannot resolve the address
    }
  } catch (error) {
    console.error("Error fetching geocoding data:", error);
    return null;
  }
};

export const fetchLocationDetailsFromBounds = async (boundsArray) => {
  if (!boundsArray || boundsArray.length === 0) {
    throw new Error("Bounds array is required.");
  }

  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

  // Calculate the center of the bounds
  const calculateCenter = (bounds) => {
    const latitudes = bounds.map((point) => point.lat);
    const longitudes = bounds.map((point) => point.lng);
    const latCenter = (Math.min(...latitudes) + Math.max(...latitudes)) / 2;
    const lngCenter = (Math.min(...longitudes) + Math.max(...longitudes)) / 2;
    return { lat: latCenter, lng: lngCenter };
  };

  const center = calculateCenter(boundsArray);

  try {
    const data = await fetchGoogleGeocodeJson({
      location: center,
      apiKey,
    });

    if (data.status !== "OK") {
      throw new Error(`Geocoding API error: ${data.status}`);
    }

    const { city, state, stateCode, zipCode, country, countryCode } =
      resolveServiceAreaLocationLabelFromGeocodeResults(data.results || []);

    const { timeZone: timeZoneId } = getTimeZone(center.lat, center.lng);
    console.log("locationServices timeZoneId: ", timeZoneId);

    // Optionally, generate geohash
    const geohash = geohashForLocation([center.lat, center.lng]);

    return {
      city,
      state,
      stateCode,
      country,
      countryCode,
      zipCode,
      geohash6: geohash.substring(0, 6),
      lat: center.lat,
      lng: center.lng,
      timeZoneId,
    };
  } catch (error) {
    console.error("Error fetching location details:", error);
    throw new Error("Error fetching location details.");
  }
};

export const getTimeZone = (latitude, longitude) => {
  try {
    const timeZone = tzlookup(latitude, longitude); // Get timezone using tz-lookup
    const currentTime = moment.tz(timeZone).format(); // Get the current time in the timezone
    return { timeZone, currentTime };
  } catch (error) {
    console.error("Error fetching timezone:", error);
    throw new Error("Unable to determine timezone.");
  }
};

function decodeGeoHashToBounds(geohash) {
  if (!geohash) return [];

  let isEven = true;
  const lat = [-90.0, 90.0];
  const lon = [-180.0, 180.0];

  for (let i = 0; i < geohash.length; i++) {
    const c = geohash[i];
    const cd = BASE32.indexOf(c);
    for (let j = 0; j < 5; j++) {
      const mask = BITS[j];
      if (isEven) {
        refineInterval(lon, cd, mask);
      } else {
        refineInterval(lat, cd, mask);
      }
      isEven = !isEven;
    }
  }
  const latCenter = (lat[0] + lat[1]) / 2;
  const lonCenter = (lon[0] + lon[1]) / 2;

  return {
    sw: { lat: lat[0], lng: lon[0] },
    ne: { lat: lat[1], lng: lon[1] },
    center: { lat: latCenter, lng: lonCenter },
  };
}

function refineInterval(interval, cd, mask) {
  if (cd & mask) {
    interval[0] = (interval[0] + interval[1]) / 2;
  } else {
    interval[1] = (interval[0] + interval[1]) / 2;
  }
}

const stateAbbreviations = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
};

export const convertStateNameToAbbreviation = (input) => {
  if (typeof input !== "string" || input.trim() === "") {
    return input;
  }

  const cleanInput = input.trim().toUpperCase();

  const stateNames = Object.keys(stateAbbreviations);
  const abbreviationValues = Object.values(stateAbbreviations);

  if (abbreviationValues.includes(cleanInput)) {
    return cleanInput;
  }

  const isoRegionMatch = cleanInput.match(/^(?:US|USA)[\s_-]*([A-Z]{2})$/);
  if (isoRegionMatch && abbreviationValues.includes(isoRegionMatch[1])) {
    return isoRegionMatch[1];
  }

  const matchedState = stateNames.find(
    (state) => state.toUpperCase() === cleanInput,
  );

  return matchedState ? stateAbbreviations[matchedState] : input.trim();
};

// Function to get members in an area
export const getMembersInArea = async (
  { geohash = null, center = null },
  radiusInMiles = 5,
) => {
  let searchCenter;

  console.log("getMembersInArea - center: ", center);

  if (geohash) {
    searchCenter = decodeGeoHash(geohash);
    console.log("using geohash");
  } else if (center && center.lat && center.lng) {
    searchCenter = [center.lat, center.lng];
    console.log("using center");
  } else {
    console.error("Either geohash or center with lat/lng is required");
    return [];
  }

  // console.log("searchCenter: ", searchCenter);

  const radiusInM = radiusInMiles * 1609.34; // Convert miles to meters
  const bounds = geohashQueryBounds(searchCenter, radiusInM);

  // console.log("bounds: ", bounds);

  let membersLocations = [];

  try {
    for (const [start, end] of bounds) {
      const q = query(
        collection(db, "membersPrivate"), // Adjust according to your collection name
        // where("status", "in", ["active", "waitlist"]), // Query for 'active' or 'waitlist'
        orderBy("location.geohash"),
        startAt(start),
        endAt(end),
      );

      const querySnapshot = await getDocs(q);

      // console.log("querySnapshot: ", querySnapshot)

      querySnapshot.forEach((doc) => {
        const memberData = doc.data();
        const memberCoordinates = {
          lat: memberData?.location?.lat,
          lng: memberData?.location?.lng,
          status: memberData?.status || "active",
        };

        membersLocations.push(memberCoordinates);
      });
    }

    return membersLocations;
  } catch (error) {
    console.error("Error getting members: ", error);
    return [];
  }
};

const hasAnyEmployees = (employeesData = {}) =>
  Boolean(
    employeesData &&
    typeof employeesData === "object" &&
    Object.keys(employeesData).length > 0,
  );

const resolveWithTimeout = async (
  promiseFactory,
  { timeoutMs = HELPER_LOCATION_LOOKUP_TIMEOUT_MS, label = "lookup" } = {},
) => {
  const safeTimeoutMs = Number(timeoutMs);
  const hasTimeout = Number.isFinite(safeTimeoutMs) && safeTimeoutMs > 0;
  let timeoutId = null;
  const lookupPromise = Promise.resolve().then(promiseFactory);

  if (!hasTimeout) {
    return lookupPromise;
  }

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const error = new Error(`${label} timed out after ${safeTimeoutMs}ms.`);
      error.code = "availability/timeout";
      reject(error);
    }, safeTimeoutMs);
  });

  try {
    return await Promise.race([lookupPromise, timeoutPromise]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const normalizeHelperLocationSearchOptions = (optionsOrRadius = {}) => {
  if (
    optionsOrRadius &&
    typeof optionsOrRadius === "object" &&
    !Array.isArray(optionsOrRadius)
  ) {
    return optionsOrRadius;
  }

  return {
    radiusInMiles: Number(optionsOrRadius || 0) || 10,
  };
};

const shouldUseCityGridHelperSearch = ({
  latitude,
  longitude,
  locationData = {},
  locationSearchMode = "",
  enableCityGridSearch = true,
} = {}) => {
  if (enableCityGridSearch === false) return false;
  if (!hasValidCoordinates(latitude, longitude)) return false;

  return isCityLevelHelperLocationSearch({
    locationData: {
      latitude,
      longitude,
      ...(locationData || {}),
    },
    locationSearchMode,
  });
};

const getLocationDataViewport = (locationData = {}) =>
  normalizeLocationViewportBounds(
    locationData?.viewport ||
      locationData?.cityViewport ||
      locationData?.geometry?.viewport ||
      locationData?.geometryViewport ||
      null,
  );

const getCityViewportLookupKey = (locationData = {}) => {
  const city = normalizeLocationText(
    locationData?.city || locationData?.locality || "",
  ).toLowerCase();
  const state = normalizeLocationText(
    locationData?.stateCode ||
      locationData?.region_code ||
      locationData?.state ||
      locationData?.region ||
      "",
  ).toLowerCase();
  const country = normalizeLocationText(
    locationData?.countryCode ||
      locationData?.country_code ||
      locationData?.country ||
      "US",
  ).toLowerCase();

  if (!city) return "";
  return [city, state, country].filter(Boolean).join("|");
};

const getCityViewportLookupAddress = (locationData = {}) => {
  const city = normalizeLocationText(
    locationData?.city || locationData?.locality || "",
  );
  const state = normalizeLocationText(
    locationData?.stateCode ||
      locationData?.region_code ||
      locationData?.state ||
      locationData?.region ||
      "",
  );
  const country = normalizeLocationText(
    locationData?.countryCode ||
      locationData?.country_code ||
      locationData?.country ||
      "US",
  );

  return [city, state, country].filter(Boolean).join(", ");
};

const fetchCityViewportJson = async (
  url,
  { timeoutMs = CITY_VIEWPORT_LOOKUP_TIMEOUT_MS } = {},
) => {
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const safeTimeoutMs = Number(timeoutMs);
  const hasTimeout = Number.isFinite(safeTimeoutMs) && safeTimeoutMs > 0;
  let timeoutId = null;

  const fetchPromise = fetch(url, {
    signal: controller?.signal,
  }).then((response) => response?.json?.());

  if (!hasTimeout) {
    return fetchPromise;
  }

  const timeoutPromise = new Promise((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      controller?.abort();
      const error = new Error("City viewport lookup timed out.");
      error.name = "AbortError";
      reject(error);
    }, safeTimeoutMs);
  });

  try {
    return await Promise.race([fetchPromise, timeoutPromise]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

const resolveCityViewportFromGoogle = async (
  locationData = {},
  {
    timeoutMs = CITY_VIEWPORT_LOOKUP_TIMEOUT_MS,
    useBrowserGeocoder = true,
  } = {},
) => {
  const lookupKey = getCityViewportLookupKey(locationData);
  if (!lookupKey) return null;
  if (CITY_VIEWPORT_CACHE.has(lookupKey)) {
    return CITY_VIEWPORT_CACHE.get(lookupKey);
  }

  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  const address = getCityViewportLookupAddress(locationData);
  if (!apiKey || !address || typeof fetch !== "function") {
    CITY_VIEWPORT_CACHE.set(lookupKey, null);
    return null;
  }

  try {
    const data = await fetchGoogleGeocodeJson({
      address,
      apiKey,
      timeoutMs,
      useBrowserGeocoder,
    });
    const result = data?.status === "OK" ? data?.results?.[0] : null;
    const resultTypes = Array.isArray(result?.types) ? result.types : [];
    const isLocalityResult =
      resultTypes.includes("locality") ||
      resultTypes.includes("postal_town") ||
      resultTypes.includes("administrative_area_level_3");
    const viewport = isLocalityResult
      ? normalizeLocationViewportBounds(result?.geometry?.viewport)
      : null;

    CITY_VIEWPORT_CACHE.set(lookupKey, viewport || null);
    return viewport || null;
  } catch {
    CITY_VIEWPORT_CACHE.set(lookupKey, null);
    return null;
  }
};

export const buildHelperCitySearchCoordinates = async ({
  latitude,
  longitude,
  locationData = {},
  cityGridOffsetMiles = HELPER_CITY_GRID_SEARCH_OFFSET_MILES,
  cityGridMaxProbes = HELPER_CITY_GRID_SEARCH_MAX_PROBES,
  enableCityViewportLookup = true,
  cityViewportLookupTimeoutMs = CITY_VIEWPORT_LOOKUP_TIMEOUT_MS,
  useBrowserGeocoder = true,
} = {}) => {
  const maxProbes = Math.max(
    0,
    Math.floor(Number(cityGridMaxProbes || HELPER_CITY_GRID_SEARCH_MAX_PROBES)),
  );
  if (maxProbes <= 0) return [];

  const existingViewport = getLocationDataViewport(locationData);
  const viewport =
    existingViewport ||
    (enableCityViewportLookup === false
      ? null
      : await resolveCityViewportFromGoogle(locationData, {
          timeoutMs: cityViewportLookupTimeoutMs,
          useBrowserGeocoder,
        }));
  const viewportProbes = buildCityViewportGridSearchCoordinates({
    latitude,
    longitude,
    viewport,
    maxProbes,
  });

  if (viewportProbes.length > 0) {
    return viewportProbes;
  }

  return buildCityGridSearchCoordinates({
    latitude,
    longitude,
    offsetMiles:
      Number(cityGridOffsetMiles || 0) || HELPER_CITY_GRID_SEARCH_OFFSET_MILES,
  }).slice(0, maxProbes);
};

const getActiveEmployeesAtExactLocation = async (lat, lng, options = {}) => {
  const memberGeohash5 = geohashForLocation([lat, lng], 5); // Convert lat/lng to geohash precision 5
  const employeesData = {};

  // Query Firestore using array-contains to match the geohash
  const q = query(
    collection(db, "employees"),
    where("status", "==", "active"),
    where("geohash5Arr", "array-contains", memberGeohash5), // Check if the geohash exists in geohash5Arr
  );

  try {
    const querySnapshot = await resolveWithTimeout(() => getDocs(q), {
      timeoutMs: options?.helperLookupTimeoutMs,
      label: options?.label || "helper availability lookup",
    });

    querySnapshot.forEach((doc) => {
      const employeeData = doc.data();
      if (employeeData?.bookingBlocked === true || isHelperSuspended(employeeData)) return;

      // new way
      const {
        inArea = false,
        end,
        start,
        futureAvailability = 0,
      } = getEmployeeStatus(
        lat,
        lng,
        getEmployeeCoverageAreas(employeeData),
        employeeData.availability,
      );
      if (shouldIncludeHelperInMemberList({ inArea })) {
        employeesData[doc.id] = {
          ...employeeData,
          startDate: start,
          endDate: end,
          futureAvailability,
        };
      }
    });
  } catch (error) {
    console.error("Error fetching employees:", error);
    throw error;
  }

  return employeesData;
};

const getActiveEmployeesAtProbeLocations = async (
  probeCoordinates = [],
  {
    helperLookupTimeoutMs = HELPER_LOCATION_LOOKUP_TIMEOUT_MS,
    probeBatchSize = HELPER_CITY_GRID_SEARCH_BATCH_SIZE,
  } = {},
) => {
  const probes = Array.isArray(probeCoordinates) ? probeCoordinates : [];
  const batchSize = Math.max(
    1,
    Math.floor(Number(probeBatchSize || HELPER_CITY_GRID_SEARCH_BATCH_SIZE)),
  );

  let lookupError = null;
  for (let index = 0; index < probes.length; index += batchSize) {
    const batch = probes.slice(index, index + batchSize);
    let pending = batch.map((probe) => ({
      probe,
      promise: getActiveEmployeesAtExactLocation(
        probe.latitude,
        probe.longitude,
        {
          helperLookupTimeoutMs,
          label: `helper availability lookup ${probe.key || "probe"}`,
        },
      ).then(
        (employees) => ({ employees }),
        (error) => ({ error }),
      ),
    }));

    while (pending.length > 0) {
      const { probe, employees, error } = await Promise.race(
        pending.map((entry) =>
          entry.promise.then((result) => ({
            probe: entry.probe,
            ...result,
          })),
        ),
      );
      pending = pending.filter((entry) => entry.probe !== probe);

      if (error) lookupError = error;
      if (hasAnyEmployees(employees)) {
        return { employees, matchedPoint: probe };
      }
    }
  }

  if (lookupError) throw lookupError;
  return { employees: {}, matchedPoint: null };
};

// Utility function to fetch employees in the area based on lat/lng.
// City/IP/SEO-route coordinates are center points, not exact addresses, so an
// empty center result fans out to a bounded city viewport grid when available,
// or to a small 5-mile compass search before showing public waitlist messaging.
// Explicit street/home addresses remain exact.
export const getActiveEmployeesAtLocationWithMatch = async (
  lat,
  lng,
  optionsOrRadius = {},
) => {
  const latitude = Number(lat);
  const longitude = Number(lng);

  if (!hasValidCoordinates(latitude, longitude)) {
    return { employees: {}, matchedPoint: null };
  }

  const options = normalizeHelperLocationSearchOptions(optionsOrRadius);
  const centerEmployees = await getActiveEmployeesAtExactLocation(
    latitude,
    longitude,
    {
      helperLookupTimeoutMs: options?.helperLookupTimeoutMs,
      label: "helper availability lookup center",
    },
  );

  if (
    hasAnyEmployees(centerEmployees) ||
    !shouldUseCityGridHelperSearch({
      latitude,
      longitude,
      ...options,
    })
  ) {
    return {
      employees: centerEmployees,
      matchedPoint: hasAnyEmployees(centerEmployees)
        ? { latitude, longitude, key: "center" }
        : null,
    };
  }

  const probeCoordinates = await buildHelperCitySearchCoordinates({
    latitude,
    longitude,
    locationData: options?.locationData || {},
    cityGridOffsetMiles: options?.cityGridOffsetMiles,
    cityGridMaxProbes: options?.cityGridMaxProbes,
    enableCityViewportLookup: options?.enableCityViewportLookup,
    cityViewportLookupTimeoutMs: options?.cityViewportLookupTimeoutMs,
    useBrowserGeocoder: options?.useBrowserGeocoder,
  });

  return getActiveEmployeesAtProbeLocations(probeCoordinates, {
    helperLookupTimeoutMs: options?.helperLookupTimeoutMs,
    probeBatchSize: options?.probeBatchSize,
  });
};

export const getActiveEmployeesAtLocation = async (lat, lng, optionsOrRadius = {}) => {
  const result = await getActiveEmployeesAtLocationWithMatch(lat, lng, optionsOrRadius);
  return result.employees;
};

export function groupGeohashBounds(geohashes, prefixLength = 4) {
  const sortedGeohashes = (Array.isArray(geohashes) ? geohashes : [])
    .map((geohash) => String(geohash || "").trim())
    .filter(Boolean)
    .sort();

  if (sortedGeohashes.length === 0) {
    return [];
  }

  const condensedBounds = [];
  let start = sortedGeohashes[0];
  let previous = sortedGeohashes[0];
  let currentPrefix = start?.substring(0, prefixLength);

  for (let i = 1; i < sortedGeohashes.length; i++) {
    const currentGeohash = sortedGeohashes[i];
    const currentGeohashPrefix = currentGeohash.substring(0, prefixLength);

    if (currentGeohashPrefix === currentPrefix) {
      // Continue the current range
      previous = currentGeohash;
    } else {
      // Save the current range and start a new one
      condensedBounds.push([start, previous]);
      start = currentGeohash;
      previous = currentGeohash;
      currentPrefix = currentGeohashPrefix;
    }
  }

  // Add the last range
  condensedBounds.push([start, previous]);

  return condensedBounds;
}

export const getEmployeeCurrentAreaGeohash5Arr = (
  employeeData = {},
  _now = DateTime.now(),
) => {
  const storedGeohash5Arr = normalizeStringArray(
    employeeData?.geohash5Arr || [],
  );
  if (storedGeohash5Arr.length > 0) {
    return storedGeohash5Arr;
  }

  const bounds = Array.isArray(employeeData?.currentArea?.bounds)
    ? employeeData.currentArea.bounds
    : [];

  if (bounds.length >= 3) {
    try {
      return normalizeStringArray(generateGeohashesInPolygon(bounds, 5));
    } catch (error) {
      console.error(
        "Failed to generate current-area geohashes from bounds:",
        error,
      );
    }
  }

  return [];
};

export const fetchMembersWithGeohash5Arr = async (employeeData) => {
  const { areas = {}, availability = {} } = employeeData || {};
  const geohash5Arr = getEmployeeCurrentAreaGeohash5Arr(employeeData);

  const condensedBounds = groupGeohashBounds(geohash5Arr);
  const allMembers = new Map();

  for (const [start, end] of condensedBounds) {
    // Query Firestore with the geohash bounds
    const q = query(
      collection(db, "membersPrivate"),
      where("status", "==", "active"),
      orderBy("location.geohash"),
      startAt(start),
      endBefore(end + "\uf8ff"), // Includes everything starting with 'end'
    );

    try {
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        const memberPrivateData = doc.data();

        const { inArea = false } = getEmployeeStatus(
          memberPrivateData?.location?.lat,
          memberPrivateData?.location?.lng,
          areas,
          availability,
        );

        // Filter members inside serviceBounds
        if (inArea) {
          allMembers.set(doc.id, memberPrivateData);
        }
      });
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  }

  // Convert map to object and return
  const aggregatedMembers = Object.fromEntries(allMembers);
  return aggregatedMembers;
};







// Helper function to check if a bounding box intersects a polygon
function doesBoundingBoxIntersectPolygon(boundingBox, polygon) {
  const [minLat, maxLat, minLng, maxLng] = boundingBox;

  // Check if any polygon vertex is inside the bounding box
  const isVertexInBoundingBox = polygon.some(({ lat, lng }) => {
    return lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng;
  });

  if (isVertexInBoundingBox) return true;

  // Check if the bounding box vertices are inside the polygon
  const boundingBoxVertices = [
    [minLat, minLng],
    [minLat, maxLng],
    [maxLat, minLng],
    [maxLat, maxLng],
  ];

  const isBoundingBoxVertexInPolygon = boundingBoxVertices.some((vertex) =>
    isPointInPolygon(vertex, polygon),
  );

  if (isBoundingBoxVertexInPolygon) return true;

  return false;
}

// Generate geohashes intersecting a polygon
export const generateGeohashesInPolygon = (polygon, precision = 5) => {
  const geohashes = new Set();

  console.log("polygon: ", polygon);

  // Step 1: Compute bounding box
  let minLat = Infinity,
    maxLat = -Infinity,
    minLng = Infinity,
    maxLng = -Infinity;

  // Use the correct structure to access lat/lng properties
  polygon.forEach(({ lat, lng }) => {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  });

  // Step 2: Get all geohashes within the bounding box
  const bboxHashes = geohash.bboxes(minLat, minLng, maxLat, maxLng, precision);
  console.log("bboxHashes: ", bboxHashes);

  // Step 3: Filter geohashes that intersect the polygon
  bboxHashes.forEach((hash) => {
    const [minLat, minLng, maxLat, maxLng] = geohash.decode_bbox(hash);
    const boundingBox = [minLat, maxLat, minLng, maxLng];

    // Use doesBoundingBoxIntersectPolygon to check intersection
    if (doesBoundingBoxIntersectPolygon(boundingBox, polygon)) {
      geohashes.add(hash);
    } else {
      console.log("Not in polygon: ", hash);
    }
  });

  return Array.from(geohashes);
};

export const generateGeohashesInRadiusFromPoint = ({
  lat,
  lng,
  radiusMiles = 15,
  precision = 5,
} = {}) => {
  const center = { lat: Number(lat), lng: Number(lng) };
  const normalizedRadius = Number(radiusMiles);
  const normalizedPrecision = Number(precision);
  if (
    !Number.isFinite(center.lat) ||
    !Number.isFinite(center.lng) ||
    !Number.isFinite(normalizedRadius) ||
    normalizedRadius <= 0
  ) {
    throw new Error("A valid center point and radius are required.");
  }

  const latitudeDelta = normalizedRadius / MILES_PER_LATITUDE_DEGREE;
  const longitudeDelta =
    normalizedRadius /
    Math.max(1, MILES_PER_LATITUDE_DEGREE * Math.cos(toRadians(center.lat)));

  return Array.from(
    new Set(
      geohash
        .bboxes(
          center.lat - latitudeDelta,
          center.lng - longitudeDelta,
          center.lat + latitudeDelta,
          center.lng + longitudeDelta,
          normalizedPrecision,
        )
        .filter((hash) => {
          const [minLat, minLng, maxLat, maxLng] = geohash.decode_bbox(hash);
          const closestPoint = {
            lat: Math.min(Math.max(center.lat, minLat), maxLat),
            lng: Math.min(Math.max(center.lng, minLng), maxLng),
          };
          return getDistanceMiles(center, closestPoint) <= normalizedRadius;
        }),
    ),
  ).sort();
};

export const generateGeohashesForEmployee = (areas, precision = 5) => {
  if (!areas || typeof areas !== "object") {
    throw new Error("Invalid areas map provided.");
  }

  const uniqueGeohashes = new Set();

  Object.entries(areas).forEach(([areaId, area]) => {
    if (area?.bounds && Array.isArray(area.bounds)) {
      try {
        // Generate geohashes for the current area's bounds
        const geohashes = generateGeohashesInPolygon(area.bounds, precision);
        geohashes.forEach((hash) => uniqueGeohashes.add(hash));
      } catch (error) {
        console.error(
          `Failed to generate geohashes for area ${areaId}:`,
          error,
        );
      }
    } else {
      console.warn(`Area ${areaId} does not have valid bounds.`);
    }
  });

  return Array.from(uniqueGeohashes); // Convert the Set to an array for output
};

export const buildHelperCoverageIndexes = (areas = {}) => {
  const geohash5Arr = generateGeohashesForEmployee(areas, 5);
  const geohash4Arr = Array.from(
    new Set(
      geohash5Arr.map((hash) => String(hash || "").slice(0, 4)).filter(Boolean),
    ),
  );

  return {
    geohash5Arr,
    geohash4Arr,
  };
};

// Function to find areas the member is in








export const getEmployeeCurrentArea = (
  employeeData = {},
  now = DateTime.now(),
) => {
  if (!employeeData?.currentArea) return null;
  const areaKey = String(employeeData?.currentArea?.key || "").trim();
  if (!areaKey) return null;

  return {
    areaKey,
    areaData: employeeData.currentArea,
  };
};









// Main function to determine availability








// Calculate the straight-line distance in miles between two geohashes.


export const formatLocationData = (locationData) => {
  if (!locationData) return "";
  const { line1, line2, city, region, state, postal, zipCode } =
    locationData || {};
  const parts = [line1, line2, city, state || region, zipCode || postal].filter(
    Boolean,
  );
  return parts.join(", ");
};

export const formatUserLocation = (location) => {
  if (!location) return "";
  const { city, state, zipCode } = location;
  const parts = [city, state, zipCode].filter(Boolean); // Include only non-empty fields
  return parts.join(", "); // Combine with a comma
};
